import React from 'react';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Paper, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';

const CodeBlock = ({ children }) => (
  <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.900', overflow: 'auto' }}>
    <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e0e0e0', whiteSpace: 'pre-wrap' }}>{children}</Typography>
  </Paper>
);

const Section = ({ title, children }) => (
  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>{title}</Typography>
    {children}
  </Paper>
);

const DetailedGuide = () => {
  const navigate = useNavigate();
  return (
    <Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h3" gutterBottom>SSATWeb — User Guide</Typography>
        <Alert severity="info" sx={{ mb: 4 }}>SSATWeb assesses SQL Server workloads for migration to Amazon RDS. The collector gathers CPU, memory, and IOPS metrics. The web app recommends the right-sized RDS instance.</Alert>

        <Section title="Prerequisites">
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Windows with PowerShell 5.1+</li>
            <li>SqlServer PowerShell module (<code>Install-Module SqlServer</code>) or the legacy SQLPS module</li>
            <li>SQL Server credentials with sysadmin rights (the collector creates a SQL Agent job and temporary tables)</li>
            <li>Network access to target SQL Server (port 1433)</li>
            <li>SQL Agent must be running on the target server</li>
          </Typography>
        </Section>

        <Section title="Collector Setup">
          <Typography paragraph>1. Download <code>SSATcollector.zip</code> from the web app</Typography>
          <Typography paragraph>2. Extract to any directory</Typography>
          <Typography paragraph>3. For several servers, edit <code>servers.txt</code> (included in the ZIP) — one SQL Server name or IP per line. For one server, type its name in the GUI instead</Typography>
        </Section>

        <Section title="Running the Collector — GUI (RunMefirst)">
          <Alert severity="success" sx={{ mb: 2 }}>RunMefirst is the recommended method. Double-click <code>RunMefirst.bat</code> to launch the GUI.</Alert>
          
          <Typography variant="h6" gutterBottom>GUI Steps:</Typography>
          <Typography component="ol" sx={{ pl: 2 }}>
            <li><strong>Authentication:</strong> Select Windows (w) or SQL Server (s)</li>
            <li><strong>SQL Server Endpoint:</strong> Enter hostname/IP, or path to <code>servers.txt</code> for multiple servers</li>
            <li><strong>Login / Password:</strong> Required if using SQL auth</li>
            <li><strong>Collection Time:</strong> Minutes to collect. Longer is more reliable: under 24 h = low confidence, 24 h+ = medium, 7 days (10080) = high, 30 days = very high</li>
            <li><strong>Database:</strong> Use <code>msdb</code> for on-premises, <code>admin</code> for Amazon RDS</li>
            <li><strong>Customer Name:</strong> Optional — the files are saved in a subfolder with this name under the output path</li>
            <li><strong>Output Path:</strong> Optional — where to save files (default: current directory)</li>
            <li>Click <strong>Start Collection</strong>. This only starts the collection: it runs in the background as a SQL Agent job for the time you entered, and the log says "Collection STARTED"</li>
            <li>When the time has passed, click <strong>Export &amp; ZIP</strong> to create the ZIP, or <strong>Cleanup</strong> to export, ZIP and remove the tables and job from the server. <strong>Terminate</strong> stops a running collection</li>
          </Typography>

          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>The output ZIP is ready to upload directly to the SSATWeb analysis tool.</Alert>

          <Typography variant="h6" gutterBottom>Amazon RDS Notes</Typography>
          <Typography component="ul" sx={{ pl: 2, mb: 3 }}>
            <li>RDS requires <strong>SQL auth</strong> (Windows auth not available)</li>
            <li>Must specify <code>-database "admin"</code> (cannot create tables in msdb on RDS)</li>
            <li>Use the same database value for all operations (deploy, export, terminate, cleanup)</li>
            <li>SQL Agent must be enabled on the RDS instance</li>
          </Typography>

          <Typography variant="h6" gutterBottom>CLI Alternative</Typography>
          <Typography paragraph>For automation or scripting, use the command line:</Typography>
          <CodeBlock>{`# Windows Auth (On-Premises / EC2)
.\\SSATcollector_launcher.ps1 -serverlist "servers.txt" -auth "w" -collectiontime 720

# SQL Auth
.\\SSATcollector_launcher.ps1 -serverlist "servers.txt" -auth "s" -login "sa" -password "YourPass" -collectiontime 720

# Amazon RDS
.\\SSATcollector_launcher.ps1 -serverlist "servers.txt" -auth "s" -login "admin" -password "YourPass" -database "admin" -collectiontime 720

# Export + Compress after collection
.\\SSATcollector_launcher.ps1 -serverlist "servers.txt" -auth "s" -login "admin" -password "YourPass" -database "admin" -export -compress

# Full workflow: Collect + Export + Compress + Cleanup
.\\SSATcollector_launcher.ps1 -serverlist "servers.txt" -auth "w" -collectiontime 720 -export -compress -cleanup`}</CodeBlock>
        </Section>

        <Section title="What Gets Collected">
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}><TableCell><strong>Metric</strong></TableCell><TableCell><strong>Source</strong></TableCell><TableCell><strong>Frequency</strong></TableCell></TableRow></TableHead>
              <TableBody>
                <TableRow><TableCell>CPU utilization</TableCell><TableCell>Ring buffer + scheduler monitor</TableCell><TableCell>Every minute</TableCell></TableRow>
                <TableRow><TableCell>Memory</TableCell><TableCell>committed_kb, PLE, stolen memory</TableCell><TableCell>Every minute</TableCell></TableRow>
                <TableRow><TableCell>IOPS</TableCell><TableCell>sys.dm_io_virtual_file_stats</TableCell><TableCell>Every minute</TableCell></TableRow>
                <TableRow><TableCell>Throughput</TableCell><TableCell>Byte reads/writes delta</TableCell><TableCell>Every minute</TableCell></TableRow>
                <TableRow><TableCell>Server info</TableCell><TableCell>CPU count, HT ratio, SQL Edition, Version</TableCell><TableCell>Once at export</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section title="Export & Compress">
          <CodeBlock>{`# Export + Compress after collection
.\\SSATcollector_launcher.ps1 -serverlist "servers.txt" -auth "s" -login "admin" -password "YourPass" -database "admin" -export -compress

# Export + Compress + Cleanup (removes tables and job)
.\\SSATcollector_launcher.ps1 -serverlist "servers.txt" -auth "s" -login "admin" -password "YourPass" -database "admin" -export -compress -cleanup`}</CodeBlock>
          <Typography variant="h6" gutterBottom>Output Files</Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li><code>ServerName_CPU_timestamp.csv</code> — CPU utilization samples</li>
            <li><code>ServerName_CPUINFO_timestamp.csv</code> — Core count, HT ratio, SQL Edition, Version</li>
            <li><code>ServerName_MEM_timestamp.csv</code> — Memory utilization samples</li>
            <li><code>ServerName_IO_timestamp.csv</code> — IOPS and throughput per database</li>
            <li><code>ServerName_STORAGE_timestamp.csv</code> — Total database size (used for the storage recommendation)</li>
          </Typography>
        </Section>

        <Section title="How the Engine Works">
          <Typography component="ol" sx={{ pl: 2 }}>
            <li><strong>Starting size:</strong> the smallest size (xlarge, 2xlarge, 4xlarge…) with at least as many vCPUs as the source server has logical CPUs. Hyperthreading is not used for sizing</li>
            <li><strong>Family:</strong> memory:CPU ratio ≤4:1 → m-family (m8i), above that → r-family (r8i)</li>
            <li><strong>Utilization scaling (P95):</strong> CPU and memory both ≥80% → one size up, r-family. CPU ≥80% → one size up, m-family. Memory ≥80% → same size, r-family. Both &lt;50% → one size down (minimum xlarge) and the memory requirement is halved</li>
            <li><strong>Memory validation:</strong> the instance must have at least the required memory (the source server's, or half of it after a scale-down); otherwise a memory-optimized family (r8i, x2m) is used</li>
            <li><strong>IOPS/Throughput:</strong> if the pick cannot deliver the observed IOPS and throughput, it scales up, then moves to x2m, then x2iedn</li>
            <li><strong>Alternatives:</strong> the same size in a different recent family (m8i, r8i, x2m), only if it still meets all requirements</li>
            <li><strong>Price:</strong> live AWS pricing for the SQL Server edition. If AWS returns no price, "Price unavailable" is shown</li>
          </Typography>
        </Section>

        <Section title="Edition Handling">
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}><TableCell><strong>Edition</strong></TableCell><TableCell><strong>Behavior</strong></TableCell></TableRow></TableHead>
              <TableBody>
                <TableRow><TableCell>Express</TableCell><TableCell>Fixed db.t3.xlarge (4 vCPU, 16 GB)</TableCell></TableRow>
                <TableRow><TableCell>Developer</TableCell><TableCell>Fixed db.r8i.xlarge for SQL Server 2022 (v16), otherwise db.r6i.xlarge (4 vCPU, 32 GB)</TableCell></TableRow>
                <TableRow><TableCell>Web</TableCell><TableCell>Max 4xlarge (16 vCPU)</TableCell></TableRow>
                <TableRow><TableCell>Standard</TableCell><TableCell>Capped at 4xlarge for m/r families (x2m up to 8xlarge)</TableCell></TableRow>
                <TableRow><TableCell>Enterprise</TableCell><TableCell>Full range up to 32xlarge (x2m / x2iedn); 96xlarge only when IOPS or throughput require it</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section title="Upload to SSATWeb">
          <Typography component="ol" sx={{ pl: 2 }}>
            <li>Go to the web app and log in</li>
            <li>Enter a <strong>Customer / Workload Name</strong> (required)</li>
            <li>Drag and drop the ZIP file(s) — multiple ZIPs for batch analysis</li>
            <li>Click <strong>Analyze</strong></li>
          </Typography>
        </Section>

        <Section title="Reading Results">
          <Typography variant="h6" gutterBottom>Single Server</Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Summary cards — CPU P95, Memory P95, estimated monthly cost</li>
            <li>Server specs — cores, memory, HT status, SQL Edition, Version</li>
            <li>Side-by-side comparison — Your Server → Recommended RDS</li>
            <li>Instance recommendations with alternatives and pricing</li>
            <li>Tooltip — hover over instance name for sizing explanation</li>
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Batch (Multiple Servers)</Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Summary cards — total servers, avg CPU/MEM, total cost</li>
            <li>Table with CPU%, MEM%, IOPS, instance, cost per server</li>
            <li>Dropdown for alternative instances per server</li>
            <li>Edition column for each server</li>
          </Typography>
        </Section>

        <Section title="Web App Features">
          <Typography component="ul" sx={{ pl: 2 }}>
            <li><strong>History</strong> — Your past analyses, with search. Click a row to reopen it, or delete it.</li>
            <li><strong>Share</strong> — Share an analysis with colleagues by email from the results page. They see it under "Shared with me".</li>
            <li><strong>Export</strong> — JSON, CSV, and PDF report downloads. Customer name in filenames.</li>
            <li><strong>Customer uploads</strong> — All uploads across all users, with filters (administrators only, in the sidebar).</li>
            <li><strong>AI Assistant</strong> — Ask questions about your analysis (@amazon.com only).</li>
            <li><strong>Live Pricing</strong> — Real-time AWS pricing via Pricing API.</li>
          </Typography>
        </Section>

        <Section title="Parameters Reference">
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}><TableCell><strong>Parameter</strong></TableCell><TableCell><strong>Required</strong></TableCell><TableCell><strong>Description</strong></TableCell></TableRow></TableHead>
              <TableBody>
                <TableRow><TableCell>-serverlist</TableCell><TableCell>Yes</TableCell><TableCell>Path to servers.txt or single server name</TableCell></TableRow>
                <TableRow><TableCell>-auth</TableCell><TableCell>Yes</TableCell><TableCell>w = Windows, s = SQL Auth</TableCell></TableRow>
                <TableRow><TableCell>-login</TableCell><TableCell>If auth=s</TableCell><TableCell>SQL login username</TableCell></TableRow>
                <TableRow><TableCell>-password</TableCell><TableCell>If auth=s</TableCell><TableCell>SQL login password</TableCell></TableRow>
                <TableRow><TableCell>-database</TableCell><TableCell>For RDS</TableCell><TableCell>Target database (default: msdb)</TableCell></TableRow>
                <TableRow><TableCell>-collectiontime</TableCell><TableCell>Deploy</TableCell><TableCell>Duration in minutes</TableCell></TableRow>
                <TableRow><TableCell>-export</TableCell><TableCell>No</TableCell><TableCell>Export collected data to CSV</TableCell></TableRow>
                <TableRow><TableCell>-compress</TableCell><TableCell>No</TableCell><TableCell>Compress into ZIP for upload</TableCell></TableRow>
                <TableRow><TableCell>-cleanup</TableCell><TableCell>No</TableCell><TableCell>Drop tables and job after export</TableCell></TableRow>
                <TableRow><TableCell>-terminate</TableCell><TableCell>No</TableCell><TableCell>Stop running collection</TableCell></TableRow>
                <TableRow><TableCell>-outputpath</TableCell><TableCell>No</TableCell><TableCell>Output directory</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section title="Troubleshooting">
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}><TableCell><strong>Issue</strong></TableCell><TableCell><strong>Solution</strong></TableCell></TableRow></TableHead>
              <TableBody>
                <TableRow><TableCell>Cannot connect</TableCell><TableCell>Check network/firewall, verify port 1433</TableCell></TableRow>
                <TableRow><TableCell>Login failed</TableCell><TableCell>Verify credentials, check auth mode</TableCell></TableRow>
                <TableRow><TableCell>No SSAT tables found</TableCell><TableCell>Use correct -database (same as deploy)</TableCell></TableRow>
                <TableRow><TableCell>Job fails immediately</TableCell><TableCell>Verify SQL Agent is running</TableCell></TableRow>
                <TableRow><TableCell>Empty export</TableCell><TableCell>Collection needs time — wait until the collection time has passed before exporting</TableCell></TableRow>
                <TableRow><TableCell>Column mismatch</TableCell><TableCell>Cleanup and redeploy (schema changed)</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Button variant="contained" size="large" onClick={() => navigate('/')} startIcon={<ArrowBack />}>Back to Main Application</Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DetailedGuide;
